/**
 * Low-level helpers over the File System Access API (Chromium-only). The vault
 * is a `FileSystemDirectoryHandle` the user picks once; we persist the handle in
 * IndexedDB so the vault reopens between sessions (with a permission re-prompt).
 *
 * Standard handle methods (`getFileHandle`, `getDirectoryHandle`, `removeEntry`,
 * `createWritable`, async-iterable `values()`) are typed by lib.dom. The two
 * non-standard bits — `window.showDirectoryPicker` and per-handle
 * `queryPermission`/`requestPermission` — are typed locally below.
 */

import { openDB, type IDBPDatabase } from "idb";
import { type Camera, type VaultTreeNode, type VaultFileKind } from "../model/types.ts";
import {
  LEGACY_TRASH_DIR,
  TRASH_DIR,
  TRASH_INDEX,
  isTrashPath,
  type TrashEntry,
} from "./trash.ts";

// ---- non-standard API typing (localized) ----------------------------------

type PermMode = "read" | "readwrite";

interface PermissionedHandle {
  queryPermission?(d?: { mode?: PermMode }): Promise<PermissionState>;
  requestPermission?(d?: { mode?: PermMode }): Promise<PermissionState>;
}

interface PickerWindow {
  showDirectoryPicker?(opts?: { mode?: PermMode }): Promise<FileSystemDirectoryHandle>;
}

/** The async-iterator helpers are part of the spec but missing from lib.dom here. */
interface IterableDirHandle {
  values(): AsyncIterableIterator<FileSystemHandle>;
}

/** Chromium can relocate a file handle without the bytes passing through JS. */
interface MovableHandle {
  move?(dest: FileSystemDirectoryHandle, name: string): Promise<void>;
}

/** True when the current browser can open a real folder as a vault. */
export function supportsFileSystemAccess(): boolean {
  return typeof (window as unknown as PickerWindow).showDirectoryPicker === "function";
}

// ---- handle persistence (IndexedDB) ---------------------------------------

const DB_NAME = "notespace";
const KV_STORE = "kv";
/** Legacy key: single persisted handle (pre-dashboard). Migrated into VAULTS_KEY. */
const VAULT_KEY = "vaultHandle";
/** Current key: the user's vault repertoire, shown on the welcome dashboard. */
const VAULTS_KEY = "vaults";

export interface StoredVault {
  id: string;
  name: string;
  handle: FileSystemDirectoryHandle;
  lastOpened: number;
  /** Vault-relative path of the last document left open (md/canvas/pdf alike),
   *  so reopening the vault resumes on the same view instead of the first file. */
  lastActivePath?: string | null;
  /**
   * Atajos del rail: lo fijado a mano y lo visitado hace poco.
   *
   * Viven acá y no en el vault a propósito: son estado de quien lee, no del
   * documento. Meterlos en la carpeta ensuciaría un vault que tiene que poder
   * abrirse en Obsidian o en un editor de texto sin archivos que no entiende
   * —el mismo criterio que la cámara y los resaltados.
   */
  pinned?: string[];
  /** Más reciente primero. */
  recents?: string[];
}

let dbp: Promise<IDBPDatabase> | null = null;
function db(): Promise<IDBPDatabase> {
  // Bump the version so the "kv" store is added alongside the existing "docs"
  // store created by IndexedDBAdapter (same DB name).
  dbp ??= openDB(DB_NAME, 2, {
    upgrade(d) {
      if (!d.objectStoreNames.contains("docs")) d.createObjectStore("docs", { keyPath: "id" });
      if (!d.objectStoreNames.contains(KV_STORE)) d.createObjectStore(KV_STORE);
    },
  });
  return dbp;
}

/**
 * Cola de escrituras del registro. Toda modificación lee la lista entera, la
 * cambia y la vuelve a escribir; dos en vuelo a la vez leen la misma versión y
 * la segunda pisa a la primera. Pasaba al cambiar de documento: el último
 * documento abierto y los recientes se guardan juntos, y los recientes se
 * llevaban puesto `lastActivePath`, así que al volver a la app abría el viejo.
 */
let registryQueue: Promise<unknown> = Promise.resolve();
function serialized<T>(fn: () => Promise<T>): Promise<T> {
  const run = registryQueue.then(fn, fn);
  registryQueue = run.catch(() => {});
  return run;
}

async function saveVaults(vaults: StoredVault[]): Promise<void> {
  await (await db()).put(KV_STORE, vaults, VAULTS_KEY);
}

/**
 * The stored vault repertoire, most recently opened first. Migrates the legacy
 * single-handle key into the list on first read.
 */
export async function listVaults(): Promise<StoredVault[]> {
  const d = await db();
  const vaults = ((await d.get(KV_STORE, VAULTS_KEY)) as StoredVault[] | undefined) ?? [];

  const legacy = (await d.get(KV_STORE, VAULT_KEY)) as FileSystemDirectoryHandle | undefined;
  if (legacy) {
    await d.delete(KV_STORE, VAULT_KEY);
    let dup = false;
    for (const v of vaults) dup ||= await v.handle.isSameEntry(legacy);
    if (!dup) {
      vaults.push({ id: crypto.randomUUID(), name: legacy.name, handle: legacy, lastOpened: 0 });
      await saveVaults(vaults);
    }
  }

  return vaults.sort((a, b) => b.lastOpened - a.lastOpened);
}

/** Add a handle to the repertoire (or refresh the existing entry). */
export function rememberVault(handle: FileSystemDirectoryHandle): Promise<StoredVault> {
  return serialized(async () => {
    const vaults = await listVaults();
    for (const v of vaults) {
      if (await v.handle.isSameEntry(handle)) {
        v.name = handle.name;
        v.lastOpened = Date.now();
        await saveVaults(vaults);
        return v;
      }
    }
    const entry: StoredVault = {
      id: crypto.randomUUID(),
      name: handle.name,
      handle,
      lastOpened: Date.now(),
    };
    vaults.push(entry);
    await saveVaults(vaults);
    return entry;
  });
}

/** Remove a vault from the repertoire (does not touch the folder on disk). */
export function forgetVault(id: string): Promise<void> {
  return serialized(async () => {
    const vaults = await listVaults();
    await saveVaults(vaults.filter((v) => v.id !== id));
  });
}

/** Mark a vault as the most recently opened. */
export function touchVault(id: string): Promise<void> {
  return serialized(async () => {
    const vaults = await listVaults();
    const v = vaults.find((x) => x.id === id);
    if (v) {
      v.lastOpened = Date.now();
      await saveVaults(vaults);
    }
  });
}

/** Guarda un cambio puntual en el registro de un vault. */
function patchVault(id: string, patch: Partial<StoredVault>): Promise<void> {
  return serialized(async () => {
    const vaults = await listVaults();
    const v = vaults.find((x) => x.id === id);
    if (!v) return;
    Object.assign(v, patch);
    await saveVaults(vaults);
  });
}

/** Remember the vault-relative path of the document left open, for next time. */
export async function saveLastActivePath(id: string, path: string | null): Promise<void> {
  await patchVault(id, { lastActivePath: path });
}

/** Lo fijado al rail de este vault, en el orden en que se muestra. */
export async function savePinned(id: string, pinned: string[]): Promise<void> {
  await patchVault(id, { pinned });
}

/** Lo visitado hace poco en este vault, más reciente primero. */
export async function saveRecents(id: string, recents: string[]): Promise<void> {
  await patchVault(id, { recents });
}

// ---- per-canvas camera persistence ----------------------------------------
// El viewport es estado de sesión, no contenido del documento: dónde estabas
// mirando no es parte del canvas y no debería viajar con el archivo ni ensuciar
// su diff. Por eso se guarda acá, indexado por path del vault.

export async function saveCamera(path: string, camera: Camera): Promise<void> {
  await (await db()).put(KV_STORE, camera, `camera:${path}`);
}

export async function loadCamera(path: string): Promise<Camera | null> {
  const c = (await (await db()).get(KV_STORE, `camera:${path}`)) as Camera | undefined;
  return c ?? null;
}

/**
 * Carry saved viewports across a move. Keyed by path, so without this a
 * relocated canvas reopens at the origin and the old key leaks forever.
 * A folder move remaps every canvas underneath it.
 */
export async function moveCamera(from: string, to: string): Promise<void> {
  const d = await db();
  const fromKey = `camera:${from}`;
  const prefix = `${fromKey}/`;
  for (const key of await d.getAllKeys(KV_STORE)) {
    if (typeof key !== "string") continue;
    let destKey: string | null = null;
    if (key === fromKey) destKey = `camera:${to}`;
    else if (key.startsWith(prefix)) destKey = `camera:${to}${key.slice(fromKey.length)}`;
    if (!destKey) continue;
    await d.put(KV_STORE, await d.get(KV_STORE, key), destKey);
    await d.delete(KV_STORE, key);
  }
}

// ---- permissions ----------------------------------------------------------

async function ensurePermission(
  handle: FileSystemDirectoryHandle,
  prompt: boolean,
): Promise<boolean> {
  const h = handle as unknown as PermissionedHandle;
  const opts = { mode: "readwrite" as const };
  if ((await h.queryPermission?.(opts)) === "granted") return true;
  if (!prompt) return false;
  return (await h.requestPermission?.(opts)) === "granted";
}

// ---- public API -----------------------------------------------------------

/** Prompt the user to pick a folder; adds it to the repertoire on success. */
export async function pickVault(): Promise<StoredVault> {
  const picker = (window as unknown as PickerWindow).showDirectoryPicker;
  if (!picker) throw new Error("File System Access API no soportada en este navegador.");
  const handle = await picker({ mode: "readwrite" });
  return rememberVault(handle);
}

/**
 * Re-request readwrite access to a stored vault. Call from a user gesture
 * (click) — Chromium rejects permission prompts without one.
 */
export async function requestVaultAccess(handle: FileSystemDirectoryHandle): Promise<boolean> {
  return ensurePermission(handle, true);
}

/**
 * Try to silently restore the most recently opened vault: only succeeds if the
 * browser still has readwrite permission (no prompt). Otherwise the welcome
 * dashboard is shown and the user re-grants with a click.
 */
export async function restoreVault(): Promise<StoredVault | null> {
  const [last] = await listVaults();
  if (!last || last.lastOpened === 0) return null;
  return (await ensurePermission(last.handle, false)) ? last : null;
}

function kindFor(name: string): VaultFileKind | null {
  if (name.endsWith(".canvas")) return "canvas";
  if (name.endsWith(".md")) return "md";
  if (name.endsWith(".pdf")) return "pdf";
  return null;
}

/** Recursively walk a directory into a tree of folders and .md/.canvas files. */
export async function walkTree(
  dir: FileSystemDirectoryHandle,
  basePath = "",
): Promise<VaultTreeNode[]> {
  const nodes: VaultTreeNode[] = [];
  for await (const entry of (dir as unknown as IterableDirHandle).values()) {
    if (entry.name.startsWith(".")) continue; // skip dotfiles/.obsidian etc.
    // The trash is the app's own storage that happens to be visible on disk, so
    // it never enters the tree: what walks the tree renames link targets, picks
    // the first document to open and answers the search palette, and none of
    // that should reach into something the user deleted.
    if (basePath === "" && entry.name === TRASH_DIR) continue;
    const path = basePath ? `${basePath}/${entry.name}` : entry.name;
    if (entry.kind === "directory") {
      const children = await walkTree(entry as FileSystemDirectoryHandle, path);
      nodes.push({ path, name: entry.name, children });
    } else {
      const kind = kindFor(entry.name);
      if (kind) nodes.push({ path, name: entry.name, kind });
    }
  }
  // Folders first, then files; alphabetical within each group.
  nodes.sort((a, b) => {
    const af = a.children ? 0 : 1;
    const bf = b.children ? 0 : 1;
    return af !== bf ? af - bf : a.name.localeCompare(b.name);
  });
  return nodes;
}

/** Resolve a vault-relative path to its parent directory handle + leaf name. */
async function resolveParent(
  root: FileSystemDirectoryHandle,
  path: string,
  create: boolean,
): Promise<{ parent: FileSystemDirectoryHandle; name: string }> {
  const parts = path.split("/").filter(Boolean);
  const name = parts.pop();
  if (!name) throw new Error(`Invalid path: "${path}"`);
  let parent = root;
  for (const part of parts) {
    parent = await parent.getDirectoryHandle(part, { create });
  }
  return { parent, name };
}

export async function readFile(root: FileSystemDirectoryHandle, path: string): Promise<string> {
  const { parent, name } = await resolveParent(root, path, false);
  const fileHandle = await parent.getFileHandle(name);
  const file = await fileHandle.getFile();
  return file.text();
}

export async function writeFile(
  root: FileSystemDirectoryHandle,
  path: string,
  contents: string,
): Promise<void> {
  const { parent, name } = await resolveParent(root, path, true);
  const fileHandle = await parent.getFileHandle(name, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(contents);
  await writable.close();
}

/** Create a file, failing if it already exists. Returns the path actually used. */
export async function createFile(
  root: FileSystemDirectoryHandle,
  path: string,
  contents: string,
): Promise<string> {
  const { parent, name } = await resolveParent(root, path, true);
  // Avoid clobbering: if it exists, suffix with " 2", " 3", …
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : "";
  let finalName = name;
  let n = 2;
  while (await existsAny(parent, finalName)) {
    finalName = `${stem} ${n}${ext}`;
    n++;
  }
  const fileHandle = await parent.getFileHandle(finalName, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(contents);
  await writable.close();
  const dir = path.slice(0, path.length - name.length);
  return `${dir}${finalName}`;
}

/** Create a folder, failing to clobber: if it exists, suffix with " 2", " 3", … */
export async function createFolder(
  root: FileSystemDirectoryHandle,
  path: string,
): Promise<string> {
  const { parent, name } = await resolveParent(root, path, true);
  let finalName = name;
  let n = 2;
  while (await existsAny(parent, finalName)) {
    finalName = `${name} ${n}`;
    n++;
  }
  await parent.getDirectoryHandle(finalName, { create: true });
  const dir = path.slice(0, path.length - name.length);
  return `${dir}${finalName}`;
}

/** Read a file's raw bytes (for images and other binary attachments). */
export async function readBinaryFile(
  root: FileSystemDirectoryHandle,
  path: string,
): Promise<Blob> {
  const { parent, name } = await resolveParent(root, path, false);
  const fileHandle = await parent.getFileHandle(name);
  return fileHandle.getFile(); // File extends Blob
}

/** Create a binary file without clobbering. Returns the path actually used. */
export async function createBinaryFile(
  root: FileSystemDirectoryHandle,
  path: string,
  data: Blob,
): Promise<string> {
  const { parent, name } = await resolveParent(root, path, true);
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : "";
  let finalName = name;
  let n = 2;
  while (await existsAny(parent, finalName)) {
    finalName = `${stem} ${n}${ext}`;
    n++;
  }
  const fileHandle = await parent.getFileHandle(finalName, { create: true });
  const writable = await fileHandle.createWritable();
  await writable.write(data);
  await writable.close();
  const dir = path.slice(0, path.length - name.length);
  return `${dir}${finalName}`;
}

/**
 * Delete a file or folder for good (folders recursively). Nothing here goes to
 * the OS trash and the vault keeps no other copy, so this is unrecoverable —
 * `trashFile` is what the UI's "Delete" is wired to. Reserved for the app's own
 * bookkeeping files, like empty highlight sidecars.
 */
export async function deleteFile(root: FileSystemDirectoryHandle, path: string): Promise<void> {
  const { parent, name } = await resolveParent(root, path, false);
  await parent.removeEntry(name, { recursive: true });
}

/**
 * Rename/move a file or folder.
 *
 * Folders have no native move in every browser, so the fallback is a recursive
 * copy followed by deleting the original. That second step is a
 * `removeEntry(…, { recursive: true })` over real user data, so it may only run
 * once the copy is known to be complete *and* known to live somewhere else.
 * Three ways it can otherwise delete the only copy, all guarded here:
 *
 *  - **Renames that only change capitalisation.** macOS (APFS) and Windows are
 *    case-insensitive, so `getDirectoryHandle("notas", { create: true })` after
 *    "Notas" hands back *the same directory*. The copy becomes a no-op and the
 *    delete then erases the folder with everything inside it. Detected with
 *    `isSameEntry` and routed through a temporary name.
 *  - **A destination inside the source.** Copying "A" into "A/B" walks into the
 *    copy it is writing, and the delete afterwards takes the original too.
 *  - **A copy that failed halfway.** Every entry is verified against the source
 *    before anything is removed; on mismatch this throws and the original is
 *    left untouched.
 *
 * Files are moved as raw bytes, never as text. Reading a PDF through
 * `file.text()` decodes it as UTF-8, turns every byte that isn't valid UTF-8
 * into U+FFFD, and writing that back produces a different (unopenable) file —
 * which is what used to blank out every relocated PDF.
 */
export async function renameFile(
  root: FileSystemDirectoryHandle,
  from: string,
  to: string,
): Promise<void> {
  if (to === from) return;
  if (to.startsWith(`${from}/`)) throw new Error(`"${from}" can't be moved inside itself.`);

  const { parent: srcParent, name: srcName } = await resolveParent(root, from, false);
  const isDir = await isDirectory(srcParent, srcName);
  const srcHandle: FileSystemHandle = isDir
    ? await srcParent.getDirectoryHandle(srcName)
    : await srcParent.getFileHandle(srcName);
  const { parent: destParent, name: destName } = await resolveParent(root, to, true);

  // Whatever already sits at the destination. If it *is* the source (a
  // case-only rename on a case-insensitive filesystem), source and destination
  // are one entry and nothing below may treat them as two places.
  const occupant = await openEntry(destParent, destName);
  const inPlace = occupant !== null && (await occupant.isSameEntry(srcHandle));

  // Native move, which is one atomic syscall: no copy, no recursive delete.
  // Chromium barely offers it — shipped only for files inside the OPFS, behind
  // a flag for files in a user folder, and not implemented for directories at
  // all. So for this vault it almost never fires and the copy below is the real
  // path. Kept because it costs one typeof check and lights up on its own the
  // day the browser catches up.
  const move = (srcHandle as unknown as MovableHandle).move;
  if (typeof move === "function") {
    try {
      await move.call(srcHandle, destParent, destName);
      return;
    } catch {
      // Not supported for this handle — fall through to the copy.
    }
  }

  // Merging a folder into an existing one and then deleting the source loses
  // whichever side lost a name collision. The caller resolves it or nothing
  // happens. Checked before the journal opens: nothing has moved yet.
  if (isDir && occupant && !inPlace) throw new Error(`"${to}" already exists.`);

  // Out to a sibling name first, so the delete of the original never targets
  // the directory the copy was just written into.
  const destDir = to.slice(0, to.length - destName.length);
  const temp = inPlace
    ? `${destDir}${await freeName(destParent, `${destName}.notespace-tmp`)}`
    : undefined;

  // Everything below moves bytes, so from here on the intent is on disk. The
  // app's own bookkeeping files are exempt: they are rebuilt from the vault, so
  // recovering them would cost more than losing them.
  const internal = from === ".notespace" || from.startsWith(".notespace/");
  const entry: JournalEntry | null = internal
    ? null
    : { id: crypto.randomUUID(), from, to, temp, phase: "copying" };
  if (entry) await journalBegin(root, entry);

  if (inPlace) {
    const tempName = temp!.slice(destDir.length);
    await copyEntry(srcHandle, destParent, tempName, isDir);
    await srcParent.removeEntry(srcName, { recursive: isDir });
    // Past this line the original is gone and `temp` is the only copy: a crash
    // now has to be finished, not rolled back.
    if (entry) await journalPhase(root, entry.id, "source-removed");
    const tempHandle: FileSystemHandle = isDir
      ? await destParent.getDirectoryHandle(tempName)
      : await destParent.getFileHandle(tempName);
    await copyEntry(tempHandle, destParent, destName, isDir);
    await destParent.removeEntry(tempName, { recursive: isDir });
    if (entry) await journalEnd(root, entry.id);
    return;
  }

  await copyEntry(srcHandle, destParent, destName, isDir);
  await srcParent.removeEntry(srcName, { recursive: isDir });
  if (entry) await journalEnd(root, entry.id);
}

/** Copy `src` to `destParent/destName`, verifying it arrived whole. */
async function copyEntry(
  src: FileSystemHandle,
  destParent: FileSystemDirectoryHandle,
  destName: string,
  isDir: boolean,
): Promise<void> {
  if (!isDir) {
    await copyFileInto(src as FileSystemFileHandle, destParent, destName);
    return;
  }
  const dest = await destParent.getDirectoryHandle(destName, { create: true });
  if (await dest.isSameEntry(src)) throw new Error(`"${destName}" resolves to the source folder itself.`);
  await copyDirInto(src as FileSystemDirectoryHandle, dest);
  await verifyDirCopy(src as FileSystemDirectoryHandle, dest);
}

async function copyFileInto(
  src: FileSystemFileHandle,
  destParent: FileSystemDirectoryHandle,
  destName: string,
): Promise<void> {
  const file = await src.getFile();
  const destHandle = await destParent.getFileHandle(destName, { create: true });
  // Chromium writes through a swap file and only swaps it in on close(), so a
  // file copied onto itself would survive there. But the spec only says that is
  // how it is "typically" implemented, and a self-copy is meaningless anyway —
  // refuse it rather than lean on an implementation detail.
  if (await destHandle.isSameEntry(src)) throw new Error(`"${destName}" resolves to the source file itself.`);
  const writable = await destHandle.createWritable();
  await writable.write(file);
  await writable.close();
  const written = await destHandle.getFile();
  if (written.size !== file.size) {
    throw new Error(`"${destName}" copied incompletely (${written.size} of ${file.size} bytes).`);
  }
}

/**
 * Every entry of `src` exists in `dest` with the same size, recursively.
 * Throws otherwise — the caller is about to delete `src` and this is the last
 * chance to stop it.
 */
async function verifyDirCopy(
  src: FileSystemDirectoryHandle,
  dest: FileSystemDirectoryHandle,
): Promise<void> {
  for await (const entry of (src as unknown as IterableDirHandle).values()) {
    if (entry.kind === "directory") {
      const child = await dest.getDirectoryHandle(entry.name).catch(() => null);
      if (!child) throw new Error(`The folder "${entry.name}" was not copied.`);
      await verifyDirCopy(entry as FileSystemDirectoryHandle, child);
    } else {
      const copied = await dest.getFileHandle(entry.name).catch(() => null);
      if (!copied) throw new Error(`The file "${entry.name}" was not copied.`);
      const original = await (entry as FileSystemFileHandle).getFile();
      const written = await copied.getFile();
      if (written.size !== original.size) {
        throw new Error(`"${entry.name}" copied incompletely.`);
      }
    }
  }
}

/** The handle at `dir/name`, whichever kind it is, or null if nothing is there. */
async function openEntry(
  dir: FileSystemDirectoryHandle,
  name: string,
): Promise<FileSystemHandle | null> {
  try {
    return await dir.getFileHandle(name);
  } catch {
    // not a file — fall through to check for a directory
  }
  try {
    return await dir.getDirectoryHandle(name);
  } catch {
    return null;
  }
}

/** `base`, or `base 2`, `base 3`… — the first name free in `dir`. */
async function freeName(dir: FileSystemDirectoryHandle, base: string): Promise<string> {
  let name = base;
  let n = 2;
  while (await existsAny(dir, name)) {
    name = `${base} ${n}`;
    n++;
  }
  return name;
}

async function isDirectory(dir: FileSystemDirectoryHandle, name: string): Promise<boolean> {
  try {
    await dir.getDirectoryHandle(name);
    return true;
  } catch {
    return false;
  }
}

async function copyDirInto(
  src: FileSystemDirectoryHandle,
  dest: FileSystemDirectoryHandle,
): Promise<void> {
  for await (const entry of (src as unknown as IterableDirHandle).values()) {
    if (entry.kind === "directory") {
      const childDest = await dest.getDirectoryHandle(entry.name, { create: true });
      await copyDirInto(entry as FileSystemDirectoryHandle, childDest);
    } else {
      await copyFileInto(entry as FileSystemFileHandle, dest, entry.name);
    }
  }
}

async function existsAny(dir: FileSystemDirectoryHandle, name: string): Promise<boolean> {
  try {
    await dir.getFileHandle(name);
    return true;
  } catch {
    // not a file — fall through to check for a directory
  }
  try {
    await dir.getDirectoryHandle(name);
    return true;
  } catch {
    return false;
  }
}

// ---- trash ----------------------------------------------------------------

/**
 * Delete by moving into the vault's trash instead of unlinking.
 *
 * `removeEntry(…, { recursive: true })` on a folder is irreversible: it doesn't
 * go through the OS trash, and this vault has no other copy. So nothing the
 * user wrote is ever unlinked — it is moved to `TRASH_DIR` and kept until they
 * empty it, from the app or from Finder. The genuinely destructive call is
 * reserved for the app's own bookkeeping files (see `deleteFile`).
 */
export async function trashFile(
  root: FileSystemDirectoryHandle,
  path: string,
): Promise<void> {
  // The trash can't hold itself, and neither can the app's own state folder.
  if (path === ".notespace" || path.startsWith(".notespace/") || isTrashPath(path)) {
    await deleteFile(root, path);
    return;
  }

  const { parent, name } = await resolveParent(root, path, false);
  const kind = (await isDirectory(parent, name)) ? "folder" : "file";
  const id = `${new Date().toISOString().replace(/[:.]/g, "-")}-${Math.random()
    .toString(36)
    .slice(2, 8)}`;

  // Same verified move as a rename: the original is only released once the copy
  // is known to be complete.
  await renameFile(root, path, `${TRASH_DIR}/${id}/${name}`);

  const index = await readTrashIndex(root);
  index.push({ id, path, name, kind, deletedAt: Date.now() });
  await writeTrashIndex(root, index);
}

/** The trash's contents, newest first — what was deleted, from where, and when.
 *  Empty when nothing was ever deleted, or when the folder was cleared by hand
 *  in Finder and the index went with it. */
export async function readTrashIndex(root: FileSystemDirectoryHandle): Promise<TrashEntry[]> {
  const index = await readTrashIndexRaw(root, TRASH_INDEX);
  return [...index].sort((a, b) => b.deletedAt - a.deletedAt);
}

async function readTrashIndexRaw(
  root: FileSystemDirectoryHandle,
  indexPath: string,
): Promise<TrashEntry[]> {
  try {
    const parsed: unknown = JSON.parse(await readFile(root, indexPath));
    return Array.isArray(parsed) ? (parsed as TrashEntry[]) : [];
  } catch {
    // No trash yet, or the index was hand-edited into something unreadable.
    // Losing the index only costs the listing, never the entries themselves:
    // they are still folders with their original names inside `TRASH_DIR`.
    return [];
  }
}

async function writeTrashIndex(
  root: FileSystemDirectoryHandle,
  entries: TrashEntry[],
): Promise<void> {
  await writeFile(root, TRASH_INDEX, JSON.stringify(entries, null, 2));
}

/**
 * Put a trashed entry back where it came from. Returns the path it landed on,
 * which is not always the one it left: the original name may have been taken
 * again since, and overwriting whatever holds it now would delete a file to
 * undelete another. Missing parent folders are recreated.
 */
export async function restoreTrashEntry(
  root: FileSystemDirectoryHandle,
  id: string,
): Promise<string> {
  const index = await readTrashIndex(root);
  const entry = index.find((e) => e.id === id);
  if (!entry) throw new Error("That entry is no longer in the trash.");

  const from = `${TRASH_DIR}/${id}/${entry.name}`;
  if (!(await openPath(root, from))) {
    // Gone from disk (emptied in Finder), so the index line is all that is
    // left. Drop it rather than leaving a row that restores nothing.
    await writeTrashIndex(root, index.filter((e) => e.id !== id));
    throw new Error(`"${entry.name}" is no longer in the trash folder.`);
  }

  const { parent, name } = await resolveParent(root, entry.path, true);
  const finalName = await freeLeafName(parent, name);
  const to = entry.path.slice(0, entry.path.length - name.length) + finalName;

  await renameFile(root, from, to);
  try {
    await deleteFile(root, `${TRASH_DIR}/${id}`); // the now-empty wrapper
  } catch {
    // Left behind at worst; it holds nothing.
  }
  await writeTrashIndex(root, index.filter((e) => e.id !== id));
  return to;
}

/** Delete one trashed entry for good. There is no copy after this. */
export async function purgeTrashEntry(
  root: FileSystemDirectoryHandle,
  id: string,
): Promise<void> {
  const index = await readTrashIndex(root);
  try {
    await deleteFile(root, `${TRASH_DIR}/${id}`);
  } catch {
    // Already gone from disk; the index line still has to go.
  }
  await writeTrashIndex(root, index.filter((e) => e.id !== id));
}

/** Empty the trash: the folder and everything in it, index included. */
export async function emptyTrash(root: FileSystemDirectoryHandle): Promise<void> {
  try {
    await deleteFile(root, TRASH_DIR);
  } catch {
    // Nothing was ever deleted in this vault.
  }
}

/**
 * Move a pre-existing hidden trash (`.notespace/trash`) into the visible one.
 * Runs on open and is a no-op for vaults that never had one. Returns how many
 * entries were relocated, for telling the user where their deleted files went.
 */
export async function migrateLegacyTrash(root: FileSystemDirectoryHandle): Promise<number> {
  const legacy = await openPath(root, LEGACY_TRASH_DIR);
  if (!legacy || legacy.kind !== "directory") return 0;

  const legacyIndex = await readTrashIndexRaw(root, `${LEGACY_TRASH_DIR}/index.json`);
  const names: string[] = [];
  for await (const entry of (legacy as unknown as IterableDirHandle).values()) {
    if (entry.kind === "directory") names.push(entry.name);
  }

  const moved: TrashEntry[] = [];
  for (const name of names) {
    // Each entry is a whole verified move, so an interrupted migration leaves
    // the rest of the trash where it is instead of half a folder.
    await renameFile(root, `${LEGACY_TRASH_DIR}/${name}`, `${TRASH_DIR}/${name}`);
    const known = legacyIndex.find((e) => e.id === name);
    if (known) moved.push(known);
  }

  if (moved.length > 0) {
    await writeTrashIndex(root, [...(await readTrashIndex(root)), ...moved]);
  }
  try {
    await deleteFile(root, LEGACY_TRASH_DIR); // only the emptied wrapper is left
  } catch {
    // Harmless leftover: an empty hidden folder.
  }
  return names.length;
}

/** A free name in `dir` based on `name`, keeping the extension: "nota.md" that
 *  is taken becomes "nota 2.md", never "nota.md 2". */
async function freeLeafName(dir: FileSystemDirectoryHandle, name: string): Promise<string> {
  if (!(await existsAny(dir, name))) return name;
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : "";
  let n = 2;
  while (await existsAny(dir, `${stem} ${n}${ext}`)) n++;
  return `${stem} ${n}${ext}`;
}

/**
 * Remove empty folders under `path`, leaving `path` itself alone.
 *
 * The highlight sidecars mirror the vault's folder structure, and clearing the
 * last highlight of a note purges its `.json` but leaves the folders that held
 * it. Nothing ever collected those, so the mirror slowly filled with empty
 * directories that looked, to anyone opening `.notespace` in Finder, like the
 * app was keeping a copy of the whole vault.
 */
export async function pruneEmptyDirs(
  root: FileSystemDirectoryHandle,
  path: string,
): Promise<void> {
  const dir = await openPath(root, path);
  if (!dir || dir.kind !== "directory") return;
  await pruneInto(dir as FileSystemDirectoryHandle);
}

/** Prune `dir`'s subtree; true when `dir` itself ended up empty. */
async function pruneInto(dir: FileSystemDirectoryHandle): Promise<boolean> {
  // Collected up front: removing entries while iterating `values()` is asking
  // the iterator to walk a directory that changes under it.
  const children: { name: string; isDir: boolean }[] = [];
  for await (const entry of (dir as unknown as IterableDirHandle).values()) {
    children.push({ name: entry.name, isDir: entry.kind === "directory" });
  }

  let left = children.length;
  for (const child of children) {
    if (!child.isDir) continue;
    const sub = await dir.getDirectoryHandle(child.name);
    if (!(await pruneInto(sub))) continue;
    await dir.removeEntry(child.name);
    left--;
  }
  return left === 0;
}

// ---- journal --------------------------------------------------------------

/**
 * A rename is copy → verify → delete, three steps a closing tab can cut in
 * half. The verification means a half-finished copy never costs data — the
 * original is only released once the copy is whole — but it does leave debris
 * at the destination that nothing would ever clean up, and in the case-only
 * path it can leave the sole surviving copy under a temporary name.
 *
 * So the intent is written down before the first byte moves and cleared once
 * the last one lands. `recoverVault` reads what's left over on the next open
 * and either finishes the move or rolls it back.
 */
const JOURNAL = ".notespace/journal.json";

interface JournalEntry {
  id: string;
  from: string;
  to: string;
  /** Full path of the scratch copy used by the case-only rename, if any. Its
   *  presence is also what marks `from` and `to` as the same entry on disk. */
  temp?: string;
  /** `copying`: the original is still intact, anything else is debris.
   *  `source-removed`: the original is gone and `temp` holds the only copy. */
  phase: "copying" | "source-removed";
}

async function readJournal(root: FileSystemDirectoryHandle): Promise<JournalEntry[]> {
  try {
    const parsed: unknown = JSON.parse(await readFile(root, JOURNAL));
    return Array.isArray(parsed) ? (parsed as JournalEntry[]) : [];
  } catch {
    return [];
  }
}

/** Rewrite the journal. Writes are atomic (swap file + replace on close), so
 *  this is never itself the thing that gets cut in half. */
async function writeJournal(
  root: FileSystemDirectoryHandle,
  entries: JournalEntry[],
): Promise<void> {
  if (entries.length === 0) {
    try {
      await deleteFile(root, JOURNAL);
    } catch {
      // Never existed. An empty journal and no journal mean the same thing.
    }
    return;
  }
  await writeFile(root, JOURNAL, JSON.stringify(entries, null, 2));
}

async function journalBegin(root: FileSystemDirectoryHandle, e: JournalEntry): Promise<void> {
  await writeJournal(root, [...(await readJournal(root)), e]);
}

async function journalPhase(
  root: FileSystemDirectoryHandle,
  id: string,
  phase: JournalEntry["phase"],
): Promise<void> {
  const entries = await readJournal(root);
  const found = entries.find((e) => e.id === id);
  if (!found) return;
  found.phase = phase;
  await writeJournal(root, entries);
}

async function journalEnd(root: FileSystemDirectoryHandle, id: string): Promise<void> {
  await writeJournal(root, (await readJournal(root)).filter((e) => e.id !== id));
}

/** The handle at `path`, whichever kind it is, or null if nothing is there. */
async function openPath(
  root: FileSystemDirectoryHandle,
  path: string,
): Promise<FileSystemHandle | null> {
  try {
    const { parent, name } = await resolveParent(root, path, false);
    return await openEntry(parent, name);
  } catch {
    // A missing intermediate folder means the path itself is missing.
    return null;
  }
}

/** Do these two paths resolve to the same entry on disk? True for "Notas" and
 *  "notas" on a case-insensitive filesystem — the check that decides whether
 *  deleting the destination would delete the source. */
async function sameEntry(
  root: FileSystemDirectoryHandle,
  a: string,
  b: string,
): Promise<boolean> {
  const ha = await openPath(root, a);
  const hb = await openPath(root, b);
  return ha !== null && hb !== null && (await ha.isSameEntry(hb));
}

/**
 * Settle whatever a previous session left mid-flight. Call once, right after
 * opening a vault and before anything reads the tree. Returns one line per
 * repair, for telling the user what happened to their files.
 */
export async function recoverVault(root: FileSystemDirectoryHandle): Promise<string[]> {
  const notes: string[] = [];

  for (const e of await readJournal(root)) {
    const label = e.from.split("/").pop();

    if (e.phase === "source-removed" && e.temp) {
      // The original is already gone and `temp` is the only copy there is.
      // Rolling back is not an option; the move has to be finished.
      if (await openPath(root, e.temp)) {
        if (await openPath(root, e.to)) await deleteFile(root, e.to); // unverified leftover
        await renameFile(root, e.temp, e.to);
        notes.push(`Finished renaming "${label}" to "${e.to.split("/").pop()}".`);
      }
    } else if (await openPath(root, e.from)) {
      // The original survived, so the destination holds an unverified partial
      // copy and can go. Except when destination *is* the original: on a
      // case-insensitive disk "MI CASA" and "mi casa" are one folder, and
      // deleting it here would be the very bug this module exists to prevent.
      if (e.temp && (await openPath(root, e.temp))) await deleteFile(root, e.temp);
      if (!e.temp && !(await sameEntry(root, e.from, e.to)) && (await openPath(root, e.to))) {
        await deleteFile(root, e.to);
      }
      notes.push(`Discarded an unfinished copy of "${label}" — it was left where it was.`);
    }
    // Neither source nor temp left: the move finished and only the note survived.

    await journalEnd(root, e.id);
  }

  return notes;
}
